import { WORKSPACE_DOMAIN } from '../../../core/policy/identity';
import { phoneSchema } from '../../../core/primitives/schema';

import type { EmployeeCorrection } from './ports/employeeAdminPort';

/**
 * Validating a committee correction to an employee record.
 *
 * Pure, so what HR is allowed to write can be tested without rendering (§5.4).
 * The rules check who may write; only this checks that the values make sense,
 * and the same phone and name rules the employee is held to apply here — a
 * correction that bypassed them would put a record into a shape the rest of
 * the system rejects.
 */

export interface CorrectionFormValues {
  readonly fullName: string;
  readonly branchId: string;
  readonly workEmail: string;
  readonly phone: string;
  readonly address: string;
  readonly declaredChildrenCount: string;
  /** '' when the roster never said, which is a real state and not a false. */
  readonly declaredMaritalStatus: string;
}

export type CorrectionFieldErrors = Partial<Record<keyof CorrectionFormValues, string>>;

/** Matches the roster: the school records four parts, sometimes more. */
const MINIMUM_NAME_PARTS = 2;

export function validateCorrection(values: CorrectionFormValues): {
  readonly errors: CorrectionFieldErrors;
  readonly valid: boolean;
} {
  const errors: CorrectionFieldErrors = {};

  const nameParts = values.fullName.trim().split(/\s+/).filter(Boolean);
  if (nameParts.length < MINIMUM_NAME_PARTS) {
    errors.fullName = 'أدخل الاسم كما هو في الكشف';
  }

  if (values.branchId === '') errors.branchId = 'اختر الفرع';

  // Optional: the roster imported 263 people with no usable number, and
  // refusing to save a name correction because a phone is missing would be the
  // wrong trade.
  if (values.phone.trim() !== '' && !phoneSchema.safeParse(values.phone).success) {
    errors.phone = 'رقم يمني من تسعة أرقام يبدأ بـ 70 أو 71 أو 73 أو 77 أو 78';
  }

  /*
   * The address must be a school one, or absent.
   *
   * It is the key an employee is matched by, so a personal address here would
   * either match nobody or hand the record to an account outside the school.
   */
  const email = values.workEmail.trim().toLowerCase();
  if (email !== '') {
    const at = email.lastIndexOf('@');
    if (at === -1 || email.slice(at + 1) !== WORKSPACE_DOMAIN) {
      errors.workEmail = `البريد يجب أن ينتهي بـ @${WORKSPACE_DOMAIN}`;
    }
  }

  const children = Number(values.declaredChildrenCount.trim());
  if (
    values.declaredChildrenCount.trim() === '' ||
    !Number.isInteger(children) ||
    children < 0 ||
    children > 30
  ) {
    errors.declaredChildrenCount = 'عدد بين 0 و 30';
  }

  return { errors, valid: Object.keys(errors).length === 0 };
}

export function toCorrection(values: CorrectionFormValues): EmployeeCorrection {
  // Parsed rather than trimmed: the schema is what turns 00967-77x into the
  // nine digits the rest of the system stores.
  const parsedPhone = phoneSchema.safeParse(values.phone);
  const phone = parsedPhone.success ? parsedPhone.data : null;
  const address = values.address.trim();

  const email = values.workEmail.trim().toLowerCase();

  return {
    fullName: values.fullName.trim().replace(/\s+/g, ' '),
    branchId: values.branchId === '' ? null : values.branchId,
    workEmail: email === '' ? null : email,
    phone,
    address: address === '' ? null : address,
    declaredChildrenCount: Number(values.declaredChildrenCount.trim()),
    declaredMaritalStatus:
      values.declaredMaritalStatus === ''
        ? null
        : values.declaredMaritalStatus === 'married',
  };
}
